import {Palette} from 'hubbl-shared';
import {User} from 'hubbl-shared';
import {AttendeeAvatar} from './AttendeeAvatar';
import styled from 'styled-components';
import {TextHeading8} from '../text/TextHeading8';
import {memo} from 'react';
import {useI18n} from '../../hooks';

const MoreContainer = styled.div`
  position: relative;

  &:hover .tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  background-color: ${Palette.almostBlack};
  color: ${Palette.white};
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out;
  z-index: 1000;

  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 4px;
    border-style: solid;
    border-color: ${Palette.almostBlack} transparent transparent transparent;
  }
`;

const AttendeeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0px;
  flex-wrap: wrap;
`;

type RequiredUserProps = Pick<User, 'id' | 'fullName' | 'photoUrl'>;

interface Props<T extends RequiredUserProps> {
  users: T[];
  moreBackgroundColor?: Palette;
  disableEmptyState?: boolean;
  truncateAt?: number;
  size?: number;
}

export const AttendeeRack = memo(
  <T extends RequiredUserProps>({
    users = [],
    moreBackgroundColor = Palette.white,
    disableEmptyState = false,
    truncateAt,
    size = 32,
  }: Props<T>) => {
    const t = useI18n();
    if (users.length === 0 && !disableEmptyState) {
      return <TextHeading8 text={t.common.none} />;
    }

    const displayUsers = truncateAt ? users.slice(0, truncateAt) : users;
    const remainingCount = truncateAt ? users.length - truncateAt : 0;

    return (
      <AttendeeContainer>
        {displayUsers.map((user, index) => (
          <AttendeeAvatar
            key={user.id}
            attendee={user}
            size={size}
            isFirst={index === 0}
          />
        ))}
        {remainingCount > 0 && (
          <MoreContainer>
            <div
              style={{
                marginLeft: '0px',
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '50%',
                border: `2px solid ${Palette.white}`,
                backgroundColor: moreBackgroundColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                color: Palette.almostBlack,
                zIndex: 999,
              }}>
              +{remainingCount}
            </div>
            <Tooltip className="tooltip">
              {remainingCount} more{' '}
              {remainingCount === 1
                ? t.attendees.morePerson
                : t.attendees.people}
            </Tooltip>
          </MoreContainer>
        )}
      </AttendeeContainer>
    );
  },
);
